import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { useSaveActionsContext } from "context/save";

import useTemplateApi from "hooks/api/private/useTemplateApi";

import { TemplateBody, TemplateDetails } from "types/api/template";

export type TemplateBodyForm = {
    name: string;
};

export type TemplateFormHook = {
    formHook: FormHookType<TemplateBodyForm>;
};

function useTemplateForm({ templateId }: { templateId?: string }): TemplateFormHook {
    const { createTemplate, updateTemplate, getOneTemplate } = useTemplateApi();

    const { addSaveAction } = useSaveActionsContext();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                name: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
            },
        };
    }, []);

    const initialValues: TemplateBodyForm = React.useMemo(() => {
        return {
            name: "",
        };
    }, []);

    const formHook = useForm<TemplateBodyForm>(schema, initialValues);

    const { showAllError, setForm, isValid, form } = formHook;

    const [templateDetails, setTemplateDetails] = React.useState<TemplateDetails | undefined>(
        undefined
    );

    React.useEffect(() => {
        if (templateId) {
            const [apiCall, abortController] = getOneTemplate({ id: templateId });

            apiCall.then(setTemplateDetails);

            return () => abortController.abort();
        }
    }, [getOneTemplate, templateId]);

    React.useEffect(() => {
        if (templateDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.name = templateDetails.name;

                return newForm;
            });
        }
    }, [setForm, templateDetails]);

    React.useEffect(() => {
        addSaveAction("useTemplateForm", () => {
            if (isValid) {
                const promises: Promise<unknown>[] = [];

                const body: TemplateBody = {
                    name: form.name,
                };

                if (templateId) {
                    const [apiCall] = updateTemplate({ id: templateId }, body);

                    promises.push(apiCall);
                } else {
                    const [apiCall] = createTemplate(body);

                    promises.push(apiCall);
                }

                return Promise.all(promises);
            } else {
                showAllError();

                return Promise.reject("INVALID_FORM");
            }
        });
    }, [
        addSaveAction,
        showAllError,
        createTemplate,
        updateTemplate,
        form.name,
        templateId,
        isValid,
    ]);

    return React.useMemo(
        (): TemplateFormHook => ({
            formHook,
        }),

        [formHook]
    );
}

export default useTemplateForm;
